import { TriangleAlert } from 'lucide-react'

import type { ReportHeader } from '@/api/reports-types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { formatDateTime } from '@/lib/datetime'

/**
 * What a report states about itself before its rows: produced when, from live data or a snapshot,
 * and — the part worth stopping on — whether every source that could contribute to it did.
 *
 * A report that could not read one upstream system still answers, because refusing outright would
 * let an outage in one corner of the system block reporting on every other. `isComplete: false` is
 * how that compromise is not allowed to pass as a whole answer: this renders as a warning naming
 * exactly which source did not answer and why, right above the figures it may have thinned.
 */
export function ReportHeaderNotice({ header }: { header: ReportHeader }) {
  const unavailable = header.sources.filter((source) => !source.isAvailable)

  return (
    <div className="space-y-2">
      <p className="text-2xs text-muted">
        {header.basis === 'Snapshot' ? 'Frozen snapshot' : 'Live'} · as of{' '}
        <span className="numeric tabular-nums">{header.asOf}</span> · produced{' '}
        <span className="numeric tabular-nums">{formatDateTime(header.generatedAtUtc)}</span>
      </p>

      {unavailable.length > 0 ? (
        <Alert tone="warning">
          <TriangleAlert aria-hidden />
          <div className="space-y-1">
            <AlertTitle>This report is not complete</AlertTitle>
            <AlertDescription>
              <ul className="list-inside list-disc space-y-0.5">
                {unavailable.map((source) => (
                  <li key={source.sourceName}>
                    <span className="font-medium">{source.sourceName}</span> could not be read
                    {source.unavailableReason ? ` — ${source.unavailableReason}` : ''}. Figures that
                    depend on it are missing or understated below, not zero.
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </div>
        </Alert>
      ) : null}
    </div>
  )
}
